import { toast } from 'react-toastify';
import { apiClient } from './client';
import { handleApiError } from './utils';

export const getCallFormById = async (id) => {
    if (!id) {
        throw new Error('Call form ID is required.');
    }

    const response = await apiClient.get(`/callform/${id}`);
    return response.data;
};

export const getDataPoolById = async (id) => {
    if (!id) {
        throw new Error('DataPool ID is required.');
    }

    const response = await apiClient.get(`/datapool/${id}`);
    return response.data;
};

export const getCallFormStates = async () => {
    const response = await apiClient.get('/callformstate');
    return response.data;
};

export const getCallFormStatePermissions = async (stateId) => {
    if (!stateId) {
        return null;
    }

    const response = await apiClient.get('/callform/state-permissions', {
        params: { stateId },
    });

    return response.data;
};

export const getCustomerById = async (id) => {
    if (!id) {
        throw new Error('Customer ID is required.');
    }

    const response = await apiClient.get(`/customer/${id}`);
    return response.data;
};

export const getLicensePlateById = async (id) => {
    if (!id) {
        throw new Error('License plate ID is required.');
    }

    const response = await apiClient.get(`/licenseplate/${id}`);
    return response.data;
};

export const submitCallForm = async ({ id, isNewFormFromDataPool, formData }) => {
    if (!formData) {
        throw new Error('Form data is required for call form submission.');
    }

    if (isNewFormFromDataPool) {
        const response = await apiClient.post('/callform', formData, {
            params: { DataPoolId: id },
        });
        return response.data;
    }

    if (id) {
        const response = await apiClient.put(`/callform/${id}`, formData);
        return response.data;
    }

    const response = await apiClient.post('/callform', formData);
    return response.data;
};

export const createExpertReport = async ({ apiEndpoint, callFormId, reportTemplateId, accidentFileId }) => {
    if (!apiEndpoint) {
        toast.error("Rapor oluşturmak için API endpoint'i gereklidir.");
        throw new Error('API endpoint is missing for expert report creation.');
    }

    if (!callFormId) {
        toast.error("Rapor oluşturmak için çağrı formu ID'si gereklidir.");
        throw new Error('Call form ID is missing for expert report creation.');
    }

    if (!reportTemplateId) {
        toast.error('Rapor oluşturmak için şablon ID\'si gereklidir.');
        throw new Error('Report template ID is missing for expert report creation.');
    }

    try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            toast.error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
            throw new Error('Authorization token is missing.');
        }

        const queryParams = new URLSearchParams({
            callFormId: callFormId,
            reportTemplateId: reportTemplateId
        });

        if (token) {
            queryParams.append('accessToken', token);
        }

        if (accidentFileId) {
            queryParams.append('accidentFileId', accidentFileId);
        }

        const reportUrl = `${apiEndpoint}?${queryParams.toString()}`;

        const newWindow = window.open(reportUrl, '_blank');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            toast.error('Popup engellenmiş. Lütfen tarayıcınızda popup engelini kaldırın.');
            throw new Error('Popup blocked');
        }

        toast.success('Rapor yeni sekmede açılıyor...');

        return { success: true };

    } catch (error) {
        if (error.message !== 'Popup blocked') {
            toast.error('Rapor oluşturulurken bir hata oluştu.');
        }
        throw error;
    }
};

export const createCallFormFromLicensePlate = async ({
    licensePlate,
    appointedUser,
    accidentDate,
    ourFaultRate,
    willLorBeWorked
}) => {
    if (!licensePlate) {
        toast.error('Plaka numarası gereklidir.');
        throw new Error('License plate is missing for call form creation.');
    }

    if (!appointedUser) {
        toast.error('Personel seçimi gereklidir.');
        throw new Error('Appointed user is missing for call form creation.');
    }

    if (!accidentDate) {
        toast.error('Kaza tarihi gereklidir.');
        throw new Error('Accident date is missing for call form creation.');
    }

    if (ourFaultRate === null || ourFaultRate === undefined) {
        toast.error('Kusur oranı gereklidir.');
        throw new Error('Our fault rate is missing for call form creation.');
    }

    if (willLorBeWorked === null || willLorBeWorked === undefined) {
        toast.error('LOR çalışılacak mı bilgisi gereklidir.');
        throw new Error('Will LOR be worked is missing for call form creation.');
    }

    try {
        const endpoint = '/CallForm/create-callform-from-licenseplate';

        const requestBody = {
            licensePlate: licensePlate,
            appointedUser: appointedUser,
            accidentDate: accidentDate,
            ourFaultRate: parseFloat(ourFaultRate) || 0,
            willLorBeWorked: Boolean(willLorBeWorked)
        };

        const response = await apiClient.post(endpoint, requestBody);
        return response.data;

    } catch (error) {
        handleApiError(error, 'Çağrı formu oluşturulurken hata oluştu.', {
            toastPrefix: 'Çağrı formu oluşturulurken hata: '
        });

        throw error;
    }
};

export const createAccidentFileFromExpert = async ({ callFormId, fileTypeAndExpense, withReport, withFault, withoutReport }) => {
    if (!callFormId) {
        toast.error("Kaza dosyası oluşturmak için çağrı formu ID'si gereklidir.");
        throw new Error('Call form ID is missing for create accident file operation.');
    }

    if (!fileTypeAndExpense || !Array.isArray(fileTypeAndExpense) || fileTypeAndExpense.length === 0) {
        toast.error('Dosya türü ve masraf bilgileri gereklidir.');
        throw new Error('File type and expense data is missing for create accident file operation.');
    }

    try {
        const endpoint = `/CallForm/create-accidentfile-from-expert/${callFormId}`;

        const requestBody = {
            fileTypeAndExpense,
            withReport: Boolean(withReport),
            withFault: Boolean(withFault),
            withoutReport: Boolean(withoutReport)
        };

        const response = await apiClient.post(endpoint, requestBody);
        if (response.status >= 200 && response.status < 300) {
            toast.success('Kaza dosyası başarıyla oluşturuldu!');
            return response.data;
        } else {
            throw new Error(`Unexpected response status: ${response.status}`);
        }

    } catch (error) {
        handleApiError(error, 'Kaza dosyası oluşturulurken hata oluştu.', {
            toastPrefix: 'Kaza dosyası oluşturulurken hata: '
        });

        throw error;
    }
};

export const createLorOnlyAccidentFile = async ({ callFormId, numberOfDays, expensePerDay }) => {
    if (!callFormId) {
        toast.error("Çağrı formu ID'si gereklidir.");
        throw new Error('Call form ID is missing for create LOR accident file.');
    }
    if (!numberOfDays || !expensePerDay) {
        toast.error('Gün sayısı ve gün başı fiyat zorunludur.');
        throw new Error('Missing numberOfDays or expensePerDay.');
    }

    try {
        const endpoint = `/CallForm/create-lor-only-accidentfile/${callFormId}`;
        const response = await apiClient.post(endpoint, {
            numberOfDays: parseInt(numberOfDays, 10),
            expensePerDay: parseFloat(expensePerDay)
        });
        if (response.status >= 200 && response.status < 300) {
            toast.success(response.data?.message || 'Hak Mahrumiyeti dosyası oluşturuldu ve icra sürecine yönlendirildi.');
            return response.data;
        }
        throw new Error(`Unexpected response status: ${response.status}`);
    } catch (error) {
        handleApiError(error, 'Hak Mahrumiyeti dosyası oluşturulurken hata oluştu.', {
            toastPrefix: 'Hak Mahrumiyeti: '
        });
        throw error;
    }
};

export const createAccidentFileFromInsurance = async ({ accidentFileId }) => {
    if (!accidentFileId) {
        toast.error('Sigorta başvurusu oluşturmak için kaza dosyası ID\'si gereklidir.');
        throw new Error('Accident file ID is missing for create accident file from insurance operation.');
    }

    try {
        const endpoint = `/CallForm/create-accidentfile-from-insurance/${accidentFileId}`;

        const response = await apiClient.post(endpoint);

        if (response.status >= 200 && response.status < 300) {
            toast.success('Yeni sigorta başvurusu başarıyla oluşturuldu!');
            return response.data;
        } else {
            throw new Error(`Unexpected response status: ${response.status}`);
        }

    } catch (error) {
        handleApiError(error, 'Sigorta dosyası oluşturulurken hata oluştu.', {
            toastPrefix: 'Sigorta dosyası oluşturulurken hata: '
        });

        throw error;
    }
};
